// "Define Cost Realizations" dialog (see cost_realizations.py): platform ->
// GET .../cost-realizations/entries (the platform's Compartment Report /
// Cash Movement rows, so the user can browse for the payment(s) that
// realized a simulated cost) -> tick row(s) -> quarter/amount are
// auto-suggested from the selection (quarter from date, amount from the
// summed row amounts) but stay editable -> pick cost_items (from
// cost_item_options) -> free-text note -> POST /cost-realizations.

import React, { useEffect, useState, useCallback } from 'react';
import axiosWithAuth from '../../../helpers/AxiosHelper';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import LoadingState from '../../common/LoadingState';
import errorMessage from '../../../helpers/errorMessage';

const PLATFORMS = ['FINBEE', 'VAURAUS', 'SAVY', 'MOZZENO'];

const quarterOf = (isoDate) => {
  const d = new Date(isoDate);
  if (isNaN(d.getTime())) return null;
  const q = Math.floor(d.getMonth() / 3) + 1;
  return `${d.getFullYear()}-Q${q}`;
};

const descLine = (row) => [row.desc1, row.desc2, row.desc3, row.desc4, row.desc5].filter(Boolean).join(' / ');

const CostRealizationDialog = ({ reportingId, visible, onHide, onSaved }) => {
  const [platform, setPlatform] = useState(null);
  const [entriesData, setEntriesData] = useState(null); // {entries, quarter_options, cost_item_options}
  const [selectedRows, setSelectedRows] = useState([]);
  const [quarter, setQuarter] = useState(null);
  const [amount, setAmount] = useState(null);
  const [costItems, setCostItems] = useState([]);
  const [note, setNote] = useState('');
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const resetSelection = () => {
    setSelectedRows([]);
    setQuarter(null);
    setAmount(null);
    setCostItems([]);
    setNote('');
    setError(null);
  };

  useEffect(() => {
    if (!visible) {
      setPlatform(null);
      setEntriesData(null);
      resetSelection();
    }
  }, [visible]);

  const fetchEntries = useCallback(
    async (p) => {
      if (!reportingId || !p) return;
      setLoadingEntries(true);
      setEntriesData(null);
      resetSelection();
      try {
        const res = await axiosWithAuth.get(`/compartment-report/reportings/${reportingId}/cost-realizations/entries`, {
          params: { platform: p },
        });
        // entries have no natural unique id (several rows can share the
        // same date) - tag a stable client-side key so DataTable checkbox
        // selection identity works.
        const entries = (res.data.entries || []).map((row, i) => ({ ...row, _key: i }));
        setEntriesData({ ...res.data, entries });
      } catch (err) {
        console.error('[CostRealizationDialog] entries fetch error:', errorMessage(err));
        setError(errorMessage(err, 'Failed to load cash movement entries.'));
      } finally {
        setLoadingEntries(false);
      }
    },
    [reportingId]
  );

  const handlePlatformChange = (p) => {
    setPlatform(p);
    fetchEntries(p);
  };

  const handleSelectionChange = (e) => {
    const rows = e.value;
    setSelectedRows(rows);
    if (rows.length === 0) return;
    const quarters = new Set(rows.map((r) => quarterOf(r.date)).filter(Boolean));
    setQuarter(quarters.size === 1 ? [...quarters][0] : null);
    const sum = rows.reduce((acc, r) => acc + Math.abs(r.amount || 0), 0);
    setAmount(Math.round(sum * 100) / 100);
  };

  const canSubmit = platform && quarter && costItems.length > 0 && amount > 0;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await axiosWithAuth.post('/compartment-report/cost-realizations', {
        reporting_id: reportingId,
        platform,
        quarter,
        cost_items: costItems,
        amount,
        note: note || undefined,
      });
      if (onSaved) onSaved(res.data.realizations);
      onHide();
    } catch (err) {
      console.error('[CostRealizationDialog] submit error:', errorMessage(err));
      setError(errorMessage(err, 'Failed to mark cost as realized.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog header="Define Cost Realizations" visible={visible} onHide={onHide} style={{ width: '60rem' }} maximizable>
      <div className="cr-form-row mb-3">
        <div>
          <div className="cr-field-label">Originator</div>
          <Dropdown
            value={platform}
            options={PLATFORMS.map((p) => ({ label: p, value: p }))}
            onChange={(e) => handlePlatformChange(e.value)}
            placeholder="Select Originator"
            style={{ minWidth: '12rem' }}
          />
        </div>
      </div>

      {error && <div className="ex-inline-error mb-3">{error}</div>}
      {loadingEntries && <LoadingState label="Loading entries" />}

      {entriesData && (
        <>
          <DataTable
            value={entriesData.entries}
            dataKey="_key"
            size="small"
            showGridlines
            stripedRows
            paginator
            rows={10}
            selection={selectedRows}
            onSelectionChange={handleSelectionChange}
          >
            <Column selectionMode="multiple" style={{ width: '3rem' }} />
            <Column field="date" header="Date" bodyClassName="cr-nowrap-cell" />
            <Column field="amount" header="Amount" headerClassName="cr-num" bodyClassName="cr-num" body={(r) => (r.amount ?? '').toLocaleString?.('en-IE', { minimumFractionDigits: 2 }) ?? r.amount} />
            <Column header="Description" body={descLine} />
          </DataTable>

          <div className="cr-form-row align-items-end mt-3">
            <div>
              <div className="cr-field-label">Quarter</div>
              <Dropdown
                value={quarter}
                options={(entriesData.quarter_options || []).map((q) => ({ label: q.label, value: q.id }))}
                onChange={(e) => setQuarter(e.value)}
                placeholder="Select quarter"
                style={{ minWidth: '10rem' }}
              />
            </div>
            <div>
              <div className="cr-field-label">Cost Item(s)</div>
              <MultiSelect
                value={costItems}
                options={(entriesData.cost_item_options || []).map((c) => ({ label: c.label, value: c.id }))}
                onChange={(e) => setCostItems(e.value)}
                placeholder="Select cost item(s)"
                style={{ minWidth: '16rem' }}
              />
            </div>
            <div>
              <div className="cr-field-label">Amount</div>
              <InputNumber value={amount} onValueChange={(e) => setAmount(e.value)} mode="decimal" minFractionDigits={2} maxFractionDigits={2} />
            </div>
            <div style={{ flexGrow: 1, minWidth: '14rem' }}>
              <div className="cr-field-label">Note (optional)</div>
              <InputTextarea value={note} onChange={(e) => setNote(e.target.value)} rows={1} style={{ width: '100%' }} />
            </div>
          </div>
        </>
      )}

      <div className="cr-dialog-actions mt-4">
        <Button label="Cancel" className="p-button-text" onClick={onHide} />
        <Button label="Mark as Realized" onClick={handleSubmit} disabled={!canSubmit || submitting} loading={submitting} />
      </div>
    </Dialog>
  );
};

export default CostRealizationDialog;
